import {
  DEFAULT_TELEMETRY_PRIVACY_POLICY,
  findTelemetryPrivacyViolations,
  redactTelemetryPayload,
  type TelemetryPrivacyPolicy,
} from "../privacy";

export interface UiTelemetryEvent {
  readonly sequence: number;
  readonly eventName: string;
  readonly occurredAtEpochMs: number;
  readonly properties: Readonly<Record<string, unknown>>;
}

export interface TelemetryCollectorOptions {
  readonly clock?: () => number;
  readonly privacyPolicy?: TelemetryPrivacyPolicy;
  readonly maximumEvents?: number;
}

export const REQUIRED_UI_TELEMETRY_EVENTS = Object.freeze([
  "patient_service_opened", "emergency_action_rendered", "emergency_action_selected",
  "assessment_started", "safety_question_rendered", "question_rendered", "answer_submitted",
  "unknown_answer_selected", "speech_input_started", "speech_input_cancelled",
  "transcription_reviewed", "interpretation_changed", "validation_error_shown", "back_selected",
  "draft_saved", "draft_recovered", "network_lost", "network_restored",
  "emergency_interruption_received", "check_answers_opened", "disposition_rendered",
  "care_action_selected", "safety_net_opened", "summary_shared", "assessment_abandoned",
  "professional_case_opened", "stale_result_detected", "evidence_viewed", "evidence_corrected",
  "semantic_interpretation_resolved", "posterior_viewed", "candidate_set_viewed",
  "model_adequacy_viewed", "next_action_viewed", "professional_decision_recorded",
  "professional_override_recorded", "handoff_reviewed", "handoff_sent", "audit_opened",
  "route_viewed", "diagnosis_requested", "diagnosis_response_validated",
  "diagnosis_response_rejected", "stale_result_discarded", "candidate_explanation_opened",
  "model_adequacy_notice_viewed", "emergency_action_used",
] as const);

export { TelemetryProvider, useUiTelemetry, type TelemetryContextValue } from "./provider";

const EVENT_NAME = /^[a-z][a-z0-9_]{0,63}$/;

function stableCopy(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(stableCopy);
  }
  if (typeof value === "object" && value !== null) {
    return Object.fromEntries(
      Object.keys(value)
        .sort()
        .map((key) => [key, stableCopy((value as Record<string, unknown>)[key])]),
    );
  }
  return value;
}

export class TelemetryCollector {
  readonly #clock: () => number;
  readonly #privacyPolicy: TelemetryPrivacyPolicy;
  readonly #maximumEvents: number;
  readonly #events: UiTelemetryEvent[] = [];
  #sequence = 0;

  public constructor(options: TelemetryCollectorOptions = {}) {
    this.#clock = options.clock ?? Date.now;
    this.#privacyPolicy = options.privacyPolicy ?? DEFAULT_TELEMETRY_PRIVACY_POLICY;
    this.#maximumEvents = options.maximumEvents ?? 1_000;
    if (!Number.isInteger(this.#maximumEvents) || this.#maximumEvents <= 0) {
      throw new RangeError("maximumEvents must be a positive integer");
    }
  }

  public record(
    eventName: string,
    properties: Readonly<Record<string, unknown>> = {},
  ): UiTelemetryEvent {
    if (!EVENT_NAME.test(eventName)) {
      throw new TypeError("telemetry event name must use lower snake case");
    }
    const occurredAtEpochMs = this.#clock();
    if (!Number.isSafeInteger(occurredAtEpochMs) || occurredAtEpochMs < 0) {
      throw new RangeError("telemetry clock must return a nonnegative integer epoch time");
    }
    const safeProperties = redactTelemetryPayload(properties, this.#privacyPolicy);
    const violations = findTelemetryPrivacyViolations(safeProperties);
    if (violations.length > 0) {
      throw new Error(
        `telemetry privacy invariant failed at ${violations[0]?.path ?? "$"}`,
      );
    }
    const event: UiTelemetryEvent = Object.freeze({
      sequence: ++this.#sequence,
      eventName,
      occurredAtEpochMs,
      properties: safeProperties,
    });
    this.#events.push(event);
    if (this.#events.length > this.#maximumEvents) {
      this.#events.shift();
    }
    return event;
  }

  public snapshot(): readonly UiTelemetryEvent[] {
    return Object.freeze(this.#events.map((event) => Object.freeze({ ...event })));
  }

  public clear(): void {
    this.#events.length = 0;
  }

  public serialise(): string {
    return JSON.stringify(stableCopy(this.#events));
  }
}
